// @ts-check
// Telefone com país separado do número: Brasil por padrão, máscara de celular ou fixo enquanto digita
// e número guardado só com dígitos (o mesmo formato que a API e o WhatsApp esperam).

/** @typedef {{ countryCode: string, callingCode: string, label: string, flag: string }} PhoneCountry */

export const DEFAULT_PHONE_COUNTRY_CODE = 'BR';
const BRAZIL_CALLING_DIGITS = '55';
// E.164: no máximo 15 dígitos com o código do país
const MAX_INTERNATIONAL_DIGITS = 15;

/** @type {PhoneCountry[]} */
export const PHONE_COUNTRIES = [
  { countryCode: 'BR', callingCode: '+55', label: 'Brasil', flag: '🇧🇷' },
  { countryCode: 'AR', callingCode: '+54', label: 'Argentina', flag: '🇦🇷' },
  { countryCode: 'BO', callingCode: '+591', label: 'Bolívia', flag: '🇧🇴' },
  { countryCode: 'CL', callingCode: '+56', label: 'Chile', flag: '🇨🇱' },
  { countryCode: 'CO', callingCode: '+57', label: 'Colômbia', flag: '🇨🇴' },
  { countryCode: 'PY', callingCode: '+595', label: 'Paraguai', flag: '🇵🇾' },
  { countryCode: 'PE', callingCode: '+51', label: 'Peru', flag: '🇵🇪' },
  { countryCode: 'UY', callingCode: '+598', label: 'Uruguai', flag: '🇺🇾' },
  { countryCode: 'VE', callingCode: '+58', label: 'Venezuela', flag: '🇻🇪' },
  { countryCode: 'US', callingCode: '+1', label: 'Estados Unidos', flag: '🇺🇸' },
  { countryCode: 'CA', callingCode: '+1', label: 'Canadá', flag: '🇨🇦' },
  { countryCode: 'MX', callingCode: '+52', label: 'México', flag: '🇲🇽' },
  { countryCode: 'PT', callingCode: '+351', label: 'Portugal', flag: '🇵🇹' },
  { countryCode: 'ES', callingCode: '+34', label: 'Espanha', flag: '🇪🇸' },
  { countryCode: 'IT', callingCode: '+39', label: 'Itália', flag: '🇮🇹' },
  { countryCode: 'FR', callingCode: '+33', label: 'França', flag: '🇫🇷' },
  { countryCode: 'DE', callingCode: '+49', label: 'Alemanha', flag: '🇩🇪' },
  { countryCode: 'GB', callingCode: '+44', label: 'Reino Unido', flag: '🇬🇧' },
  { countryCode: 'JP', callingCode: '+81', label: 'Japão', flag: '🇯🇵' },
  { countryCode: 'CN', callingCode: '+86', label: 'China', flag: '🇨🇳' },
  { countryCode: 'AO', callingCode: '+244', label: 'Angola', flag: '🇦🇴' },
  { countryCode: 'MZ', callingCode: '+258', label: 'Moçambique', flag: '🇲🇿' },
];

/**
 * País do valor guardado; registros antigos só com o código de discagem caem no primeiro país que o usa.
 * @param {{ primaryPhoneCountryCode?: string | null, primaryPhoneCallingCode?: string | null } | null | undefined} phones
 * @returns {PhoneCountry}
 */
export function getPhoneCountry(phones) {
  const byCountry = PHONE_COUNTRIES.find((country) => country.countryCode === phones?.primaryPhoneCountryCode);
  if (byCountry) return byCountry;
  const byCallingCode = PHONE_COUNTRIES.find((country) => country.callingCode === phones?.primaryPhoneCallingCode);
  return byCallingCode ?? /** @type {PhoneCountry} */ (PHONE_COUNTRIES[0]);
}

/** @param {string} countryCode */
export function findPhoneCountry(countryCode) {
  return PHONE_COUNTRIES.find((country) => country.countryCode === countryCode) ?? /** @type {PhoneCountry} */ (PHONE_COUNTRIES[0]);
}

/**
 * Dígitos do número local. No Brasil aceita colar com +55 ou com o zero da operadora e limita a 11 dígitos
 * (DDD + 9 dígitos do celular).
 * @param {string} text @param {string} countryCode
 */
export function toPhoneDigits(text, countryCode) {
  let digits = String(text ?? '').replace(/\D/g, '');
  if (countryCode !== DEFAULT_PHONE_COUNTRY_CODE) return digits.slice(0, MAX_INTERNATIONAL_DIGITS);
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith(BRAZIL_CALLING_DIGITS)) digits = digits.slice(2);
  if (digits.length > 11 && digits.startsWith('0')) digits = digits.replace(/^0+/, '');
  return digits.slice(0, 11);
}

/**
 * Máscara brasileira progressiva: (11) 5200-1408 para fixo e (11) 95200-1408 para celular.
 * Outros países mostram os dígitos sem máscara.
 * @param {string} digits @param {string} countryCode
 */
export function maskPhoneNumber(digits, countryCode) {
  const clean = String(digits ?? '').replace(/\D/g, '');
  if (countryCode !== DEFAULT_PHONE_COUNTRY_CODE || clean.length === 0) return clean;
  const areaCode = clean.slice(0, 2);
  const local = clean.slice(2);
  if (clean.length <= 2) return `(${areaCode}`;
  // celular tem 9 dígitos depois do DDD; até lá, o hífen fica na posição de fixo
  const splitAt = local.length > 8 ? 5 : 4;
  if (local.length <= splitAt) return `(${areaCode}) ${local}`;
  return `(${areaCode}) ${local.slice(0, splitAt)}-${local.slice(splitAt)}`;
}

/**
 * Tipo do número brasileiro completo, para a dica abaixo do campo.
 * @param {string} digits @param {string} countryCode
 * @returns {'MOBILE' | 'LANDLINE' | 'INCOMPLETE' | null}
 */
export function getBrazilianPhoneKind(digits, countryCode) {
  if (countryCode !== DEFAULT_PHONE_COUNTRY_CODE || !digits) return null;
  if (digits.length === 11 && digits[2] === '9') return 'MOBILE';
  if (digits.length === 10 && /[2-5]/.test(digits[2] ?? '')) return 'LANDLINE';
  return 'INCOMPLETE';
}

/**
 * Valor composto da API a partir do país e dos dígitos, mantendo os telefones adicionais.
 * @param {Record<string, any> | null | undefined} previous @param {string} countryCode @param {string} digits
 */
export function toPhonesValue(previous, countryCode, digits) {
  const country = findPhoneCountry(countryCode);
  return {
    additionalPhones: [],
    ...(previous ?? {}),
    primaryPhoneNumber: digits,
    primaryPhoneCountryCode: country.countryCode,
    primaryPhoneCallingCode: country.callingCode,
  };
}
